import { ref, readonly, type Ref } from "vue";
import { useToast } from "primevue/usetoast";
import { updateUserProfile } from "@/modules/profile/api";

interface UseSetUserPasswordReturn {
  readonly isSubmitting: Ref<boolean>;
  setPassword: (userId: number, password: string) => Promise<boolean>;
}

/**
 * Composable for setting a user's password
 */
export function useSetUserPassword(): UseSetUserPasswordReturn {
  const toast = useToast();
  const isSubmitting = ref(false);

  async function setPassword(
    userId: number,
    password: string
  ): Promise<boolean> {
    if (!userId || !password?.trim()) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "User ID and password are required",
        life: 5000,
      });
      return false;
    }

    isSubmitting.value = true;

    try {
      const result = await updateUserProfile(userId, {
        password: password.trim(),
      });

      if ("error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error || "Failed to set password",
          life: 5000,
        });
        return false;
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Password has been set successfully",
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error("Error setting password:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "An unexpected error occurred",
        life: 5000,
      });
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    isSubmitting: readonly(isSubmitting),
    setPassword,
  };
}
